import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Battery, Signal, FileDown, Loader2, LayoutGrid, List, History, Calendar, X, Download, Video, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, startOfDay, endOfDay } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { DEVICE_HISTORY_API_URL } from "@/constants/system";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { generateTimelapse, TimelapseQuality } from "@/utils/timelapseGenerator";
import Zoom from 'react-medium-image-zoom';
import { Badge } from "@/components/ui/badge";
import 'react-medium-image-zoom/dist/styles.css';
import { useRoleFlags } from "@/hooks/use-role-flags";
import { ReprocessImageDialog } from "./ReprocessImageDialog";
import { DownloadImageButton } from "./DownloadImageButton";

// Defensive parser for notification.data — handles JSON-string payloads and
// rejects char-indexed corrupt shapes like {"0":"{","1":"\"","2":"i",...}
function normalizeTagData(raw: unknown): [string, unknown][] {
  if (!raw) return [];
  let value: unknown = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return []; }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const entries = Object.entries(value as Record<string, unknown>);
  const looksCharIndexed =
    entries.length > 0 &&
    entries.every(([k, v], i) =>
      k === String(i) && typeof v === 'string' && (v as string).length === 1
    );
  if (looksCharIndexed) return [];
  return entries;
}

interface DeviceHistoryItem {
  imageWatermarkFree?: string | { url?: string } | null;
  imageKit: {
    url: string;
    thumbnailUrl?: string;
    imageWatermarkFree?: string;
    watermarkFreeUrl?: string;
  };
  notification: {
    result: string;
    reason: string;
    subject: string;
    data?: Record<string, string | number>;
  };
  battery: {
    batterySoC: string;
    batteryVoltage?: string;
    chargeVin?: string;
  };
  cellular: {
    cellularStrength: string;
    cellularRat: string;
    cellularQuality?: string;
  };
  deviceLastUpdated: string;
  onDemandUserId: string;
  deviceStatus?: string;
  ambientLux?: string;
  camera?: {
    imageLocation?: string;
    imageToken?: string;
    imageUrl?: string;
  };
}

interface GridLayoutProps {
  deviceId: string;
  deviceName?: string;
  deviceDescription?: string;
  layoutType?: "grid" | "list" | "timeline";
  itemLimit?: string;
  startDate?: Date;
  endDate?: Date;
  isMobileView?: boolean;
  timelapseQuality?: TimelapseQuality;
  showOnlyAlerts?: boolean;
  deviceStatus?: string;
}

type LayoutType = "grid" | "list" | "timeline";

const getCellularNetworkType = (rat: string) => {
  const types: { [key: string]: string } = {
    "GSM": "2G",
    "GPRS": "2G",
    "EDGE": "2.5G",
    "LTE": "4G",
    "LTE-M": "4G",
    "NB-IOT": "3G",
  };
  return types[rat?.toUpperCase()] || rat;
};

const PROXIED_HOSTS = new Set([
  'storage.googleapis.com',
  'snapshot-live-v1.firebasestorage.app',
  'firebasestorage.googleapis.com',
  'image.snapshot.systems',
]);

// Route image URLs through our image-proxy edge function so the browser can
// fetch() them (the upstream hosts don't return CORS headers).
const proxiedImageUrl = (url?: string): string => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    if (!PROXIED_HOSTS.has(parsed.hostname)) return url;
    const base = import.meta.env.VITE_SUPABASE_URL || "https://ndqudoynysmynztrepwj.supabase.co";
    return `${base}/functions/v1/image-proxy?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

export const GridLayout = ({ 
  deviceId, 
  deviceName, 
  deviceDescription,
  layoutType: initialLayoutType = "grid",
  itemLimit: initialItemLimit = "25",
  startDate: initialStartDate,
  endDate: initialEndDate,
  isMobileView = false,
  timelapseQuality: initialTimelapseQuality = 'medium',
  showOnlyAlerts: initialShowOnlyAlerts = false
}: GridLayoutProps) => {
  const { isSuperuser, isAdmin } = useRoleFlags();
  const [selectedImage, setSelectedImage] = useState<DeviceHistoryItem | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [itemLimit, setItemLimit] = useState(initialItemLimit);
  const [layoutType, setLayoutType] = useState<LayoutType>(initialLayoutType);
  const [startDate, setStartDate] = useState<Date | undefined>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);
  const [pendingStartDate, setPendingStartDate] = useState<Date | undefined>(initialStartDate);
  const [pendingEndDate, setPendingEndDate] = useState<Date | undefined>(initialEndDate);
  const [isGeneratingTimelapse, setIsGeneratingTimelapse] = useState(false);
  const [timelapseQuality, setTimelapseQuality] = useState<TimelapseQuality>(initialTimelapseQuality);
  const [showOnlyAlerts, setShowOnlyAlerts] = useState(initialShowOnlyAlerts);
  const [startDatePopoverOpen, setStartDatePopoverOpen] = useState(false);
  const [endDatePopoverOpen, setEndDatePopoverOpen] = useState(false);
  const [highlightAlerts, setHighlightAlerts] = useState(true);
  const { toast } = useToast();

  // Helper function to determine image highlighting style
  const getHighlightStyle = (notificationResult: string, onDemandUserId: string) => {
    if (notificationResult?.toLowerCase() === "true") {
      return "brand-alert-tile border-red-500 bg-red-50"; // Red for alerts
    }
    if (notificationResult === "false" && onDemandUserId !== 'NULL') {
      return "brand-false-alert-tile border-blue-500 bg-blue-50"; // Blue for false alerts on on-demand images
    }
    return "border-border bg-card"; // Normal state
  };

  // Update state when props change (for external control)
  useEffect(() => {
    setLayoutType(initialLayoutType);
  }, [initialLayoutType]);

  useEffect(() => {
    setItemLimit(initialItemLimit);
  }, [initialItemLimit]);

  useEffect(() => {
    setStartDate(initialStartDate);
    setPendingStartDate(initialStartDate);
  }, [initialStartDate]);

  useEffect(() => {
    setEndDate(initialEndDate);
    setPendingEndDate(initialEndDate);
  }, [initialEndDate]);

  useEffect(() => {
    setTimelapseQuality(initialTimelapseQuality);
  }, [initialTimelapseQuality]);
  
  // Add a new useEffect to synchronize showOnlyAlerts with its prop
  useEffect(() => {
    setShowOnlyAlerts(initialShowOnlyAlerts);
  }, [initialShowOnlyAlerts]);

  const { data: historyData, refetch } = useQuery({
    queryKey: ["device-history", deviceId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      
      if (startDate) {
        params.append("startDate", startOfDay(startDate).toISOString());
      }
      if (endDate) {
        params.append("endDate", endOfDay(endDate).toISOString());
      }

      const response = await fetch(DEVICE_HISTORY_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch device history");
      }

      const data = await response.json();
      return data as DeviceHistoryItem[];
    },
  });

  const filteredAndSortedHistory = useMemo(() => {
    if (!historyData) return [];

    let sorted = [...historyData].sort((a, b) => 
      new Date(b.deviceLastUpdated).getTime() - new Date(a.deviceLastUpdated).getTime()
    );

    // Apply alerts filter if enabled
    if (showOnlyAlerts) {
      sorted = sorted.filter(item => item.notification.result?.toLowerCase() === "true");
    }

    if (startDate || endDate) {
      return sorted.filter(item => {
        const itemDate = new Date(item.deviceLastUpdated);
        if (startDate && endDate) {
          return itemDate >= startOfDay(startDate) && itemDate <= endOfDay(endDate);
        }
        if (startDate) {
          return itemDate >= startOfDay(startDate);
        }
        if (endDate) {
          return itemDate <= endOfDay(endDate);
        }
        return true;
      });
    }

    return sorted.slice(0, parseInt(itemLimit));
  }, [historyData, startDate, endDate, itemLimit, showOnlyAlerts]);

  const clearDateRange = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setPendingStartDate(undefined);
    setPendingEndDate(undefined);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setPendingStartDate(date);
    if (date) {
      setStartDatePopoverOpen(false);
      setEndDatePopoverOpen(true);
      if (pendingEndDate) {
        setStartDate(date);
        setEndDate(pendingEndDate);
      }
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setPendingEndDate(date);
    if (date && pendingStartDate) {
      setEndDatePopoverOpen(false);
      setStartDate(pendingStartDate);
      setEndDate(date);
    }
  };

  const handlePreviousImage = () => {
    if (!selectedImage) return;
    
    const currentIndex = filteredAndSortedHistory.findIndex(
      item => item.deviceLastUpdated === selectedImage.deviceLastUpdated
    );
    
    if (currentIndex > 0) {
      setSelectedImage(filteredAndSortedHistory[currentIndex - 1]);
    }
  };

  const handleNextImage = () => {
    if (!selectedImage) return;
    
    const currentIndex = filteredAndSortedHistory.findIndex(
      item => item.deviceLastUpdated === selectedImage.deviceLastUpdated
    );
    
    if (currentIndex < filteredAndSortedHistory.length - 1) {
      setSelectedImage(filteredAndSortedHistory[currentIndex + 1]);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;
      
      if (e.key === "ArrowLeft") {
        handlePreviousImage();
      } else if (e.key === "ArrowRight") {
        handleNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, filteredAndSortedHistory]);

  const { minDate, maxDate } = useMemo(() => {
    if (!historyData || historyData.length === 0) {
      return { minDate: undefined, maxDate: undefined };
    }

    const dates = historyData.map(item => new Date(item.deviceLastUpdated));
    return {
      minDate: new Date(Math.min(...dates.map(d => d.getTime()))),
      maxDate: new Date(Math.max(...dates.map(d => d.getTime()))),
    };
  }, [historyData]);

  const handleCheckboxChange = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    const allItemIds = filteredAndSortedHistory.map(item => `${item.deviceLastUpdated}`);
    
    if (allItemIds.every(id => selectedItems.has(id))) {
      setSelectedItems(new Set());
      toast({
        description: "All items deselected",
      });
    } else {
      setSelectedItems(new Set(allItemIds));
      toast({
        description: `Selected ${allItemIds.length} items`,
      });
    }
  };

  const downloadSelectedImages = async () => {
    if (selectedItems.size === 0) {
      toast({
        description: "Please select at least one image to download.",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    try {
      const zip = new JSZip();
      const selectedImages = filteredAndSortedHistory.filter(item => 
        selectedItems.has(`${item.deviceLastUpdated}`)
      );

      for (const item of selectedImages) {
        try {
          const response = await fetch(proxiedImageUrl(item.imageKit.url));
          const blob = await response.blob();
          const fileName = `image_${format(new Date(item.deviceLastUpdated), "yyyyMMdd_HHmmss")}.jpg`;
          zip.file(fileName, blob);
        } catch (error) {
          console.error("Error downloading image:", error);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      const downloadUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${deviceName || deviceId}_selected_images.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      toast({
        description: `Successfully downloaded ${selectedItems.size} images`,
      });
    } catch (error) {
      console.error("Error creating zip file:", error);
      toast({
        description: "Error downloading images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const generateTimelapseVideo = async () => {
    if (selectedItems.size === 0) {
      toast({
        description: "Please select at least one image to create timelapse",
        variant: "destructive",
      });
      return;
    }

    if (selectedItems.size > 100) {
      toast({
        description: "Maximum of 100 images allowed for timelapse generation. Please select fewer images.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingTimelapse(true);
    try {
      console.log('🎬 Starting timelapse generation process');
      const selectedImages = filteredAndSortedHistory
        .filter(item => selectedItems.has(`${item.deviceLastUpdated}`))
        .sort((a, b) => new Date(a.deviceLastUpdated).getTime() - new Date(b.deviceLastUpdated).getTime())
        .map(item => proxiedImageUrl(item.imageKit.url));

      console.log('📸 Selected images:', { count: selectedImages.length, urls: selectedImages });

      toast({
        description: `Starting timelapse generation with ${selectedImages.length} images in ${timelapseQuality} quality. This may take a few minutes...`,
      });

      const videoBlob = await generateTimelapse(selectedImages, {
        fps: 2,
        quality: timelapseQuality,
        preserveOriginalSize: timelapseQuality === 'ultra'
      });
      console.log('✅ Video blob received:', { size: videoBlob.size, type: videoBlob.type, quality: timelapseQuality });
      
      const url = URL.createObjectURL(videoBlob);
      console.log('🔗 Created URL for video blob:', url);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${deviceName || deviceId}_timelapse_${timelapseQuality}.webm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('✅ Download initiated');

      toast({
        description: `Successfully generated ${timelapseQuality} quality timelapse from ${selectedItems.size} images`,
      });
    } catch (error) {
      console.error('❌ Error generating timelapse:', error);
      toast({
        description: error instanceof Error ? error.message : "Error generating timelapse. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingTimelapse(false);
    }
  };

  const generatePDF = async () => {
    if (!historyData) return;
    setIsGenerating(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - (2 * margin);
      let yOffset = margin;

      try {
        // Prefer the customer's organisation logo, fall back to the Snapshot Live wordmark
        let logoUrl = "https://image.snapshot.systems/Marketing/Primary%20Wordmark%20Plum.png";
        try {
          const { data: deviceRow } = await supabase
            .from("device_details")
            .select("organization_id")
            .eq("device_id", deviceId)
            .maybeSingle();
          if (deviceRow?.organization_id) {
            const { data: orgRow } = await supabase
              .from("organizations")
              .select("logo_url")
              .eq("id", deviceRow.organization_id)
              .maybeSingle();
            if (orgRow?.logo_url) logoUrl = orgRow.logo_url;
          }
        } catch (orgError) {
          console.error("Error loading organisation logo:", orgError);
        }

        const logoResponse = await fetch(proxiedImageUrl(logoUrl));
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });

        // Fit inside a 50 x 15 mm box while preserving aspect ratio
        const boxWidth = 50;
        const boxHeight = 15;
        let logoWidth = boxWidth;
        let logoHeight = boxHeight;
        try {
          const props = pdf.getImageProperties(logoBase64 as string);
          const ratio = props.width / props.height;
          if (ratio >= boxWidth / boxHeight) {
            logoWidth = boxWidth;
            logoHeight = boxWidth / ratio;
          } else {
            logoHeight = boxHeight;
            logoWidth = boxHeight * ratio;
          }
        } catch {
          // keep default box dimensions
        }

        pdf.addImage(
          logoBase64 as string,
          pageWidth - margin - logoWidth,
          yOffset,
          logoWidth,
          logoHeight
        );
      } catch (error) {
        console.error("Error loading logo:", error);
      }

      const statusColumnWidth = 45;
      const mainContentWidth = contentWidth * 0.75;
      const logoSpace = 60;
      const maxTextWidth = mainContentWidth - logoSpace - 10;

      pdf.setFontSize(24);
      pdf.setTextColor(26, 31, 44);
      const titleText = deviceName || deviceId;
      const maxTitleWidth = pageWidth - (margin * 2) - logoSpace - 10;
      const splitTitle = pdf.splitTextToSize(titleText, maxTitleWidth);
      pdf.text(splitTitle, margin, yOffset + 10);
      const extraTitleLines = splitTitle.length - 1;
      const titleLineHeight = 8;
      yOffset += extraTitleLines * titleLineHeight;

      if (deviceDescription) {
        pdf.setFontSize(12);
        pdf.setTextColor(142, 145, 150);
        const splitDesc = pdf.splitTextToSize(deviceDescription, maxTitleWidth);
        pdf.text(splitDesc, margin, yOffset + 20);
        const extraDescLines = splitDesc.length - 1;
        yOffset += 30 + (extraDescLines * 5);
      } else {
        yOffset += 20;
      }

      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      if (selectedItems.size > 0) {
        const selectedText = `Selected ${selectedItems.size} of ${filteredAndSortedHistory.length} items`;
        pdf.text(selectedText, margin, yOffset);
      } else if (startDate && endDate) {
        const dateRangeText = `Date Range: ${format(startDate, "dd/MM/yy")} - ${format(endDate, "dd/MM/yy")}`;
        pdf.text(dateRangeText, margin, yOffset);
      } else {
        const itemLimitText = `Showing last ${itemLimit} items`;
        pdf.text(itemLimitText, margin, yOffset);
      }

      pdf.setDrawColor(241, 240, 251);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yOffset + 5, pageWidth - margin, yOffset + 5);
      yOffset += 15;

      const itemsForReport = selectedItems.size > 0
        ? filteredAndSortedHistory.filter(item => selectedItems.has(`${item.deviceLastUpdated}`))
        : filteredAndSortedHistory;

      // PDF report reads oldest -> newest (UI grid stays newest-first)
      const sortedItemsForReport = [...itemsForReport].sort(
        (a, b) => new Date(a.deviceLastUpdated).getTime() - new Date(b.deviceLastUpdated).getTime()
      );

      for (const item of sortedItemsForReport) {
        if (yOffset > 250) {
          pdf.addPage();
          yOffset = margin;
        }

        const cardWidth = contentWidth;
        const hasDataTags = normalizeTagData(item.notification?.data).length > 0;
        const cardHeight = hasDataTags ? 48 : 40;
        
        if (highlightAlerts && item.notification.result?.toLowerCase() === "true") {
          pdf.setFillColor(254, 242, 242); // Red background
          pdf.rect(margin, yOffset, cardWidth, cardHeight, "F");
        } else if (item.notification.result === "false" && item.onDemandUserId && item.onDemandUserId !== "NULL") {
          pdf.setFillColor(239, 246, 255); // Blue background
          pdf.rect(margin, yOffset, cardWidth, cardHeight, "F");
        } else {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yOffset, cardWidth, cardHeight, "F");
        }

        const thumbWidth = 40;
        const thumbHeight = 30;

        // Try to load image; if it fails, draw a placeholder so the text card still renders.
        try {
          const imgData = await fetch(proxiedImageUrl(item.imageKit.thumbnailUrl));
          if (!imgData.ok) throw new Error(`Image fetch ${imgData.status}`);
          const imgBlob = await imgData.blob();
          const base64Image = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(imgBlob);
          });

          pdf.addImage(
            base64Image as string,
            "JPEG",
            margin + 5,
            yOffset + 5,
            thumbWidth,
            thumbHeight
          );
        } catch (error) {
          console.error("Error loading image for PDF, drawing placeholder:", error);
          pdf.setFillColor(229, 231, 235);
          pdf.rect(margin + 5, yOffset + 5, thumbWidth, thumbHeight, "F");
          pdf.setFontSize(8);
          pdf.setTextColor(107, 114, 128);
          pdf.text("No image", margin + 5 + thumbWidth / 2 - 6, yOffset + 5 + thumbHeight / 2);
        }

        try {
          const textX = margin + thumbWidth + 15;
          const statusX = pageWidth - margin - statusColumnWidth;
          const textWidth = (contentWidth * 0.75) - thumbWidth - 25;

          pdf.setFontSize(12);
          pdf.setTextColor(26, 31, 44);
          const splitSubject = pdf.splitTextToSize(item.notification.subject, textWidth);
          pdf.text(splitSubject, textX, yOffset + 12);

          pdf.setFontSize(8);
          pdf.setTextColor(107, 114, 128);
          const reason = item.notification.reason;
          const truncatedReason = reason.length > 200 ? reason.substring(0, 197) + '...' : reason;
          const splitReason = pdf.splitTextToSize(truncatedReason, textWidth);
          if (splitReason.length > 7) {
            const finalReason = splitReason.slice(0, 7);
            finalReason[6] = finalReason[6].replace(/(.{3})$/, '...');
            pdf.text(finalReason, textX, yOffset + 20);
          } else {
            pdf.text(splitReason, textX, yOffset + 20);
          }

          // Data tags row
          if (hasDataTags) {
            pdf.setFontSize(7);
            pdf.setTextColor(120, 120, 120);
            const tagEntries = normalizeTagData(item.notification.data);
            if (tagEntries.length > 0) {
              const tagString = tagEntries
                .slice(0, 5)
                .map(([k, v]) => `${k}: ${v}`)
                .join('  •  ');
              const splitTags = pdf.splitTextToSize(tagString, textWidth);
              pdf.text(splitTags[0], textX, yOffset + 40);
            }
          }

          pdf.setFontSize(9);
          pdf.setTextColor(26, 31, 44);
          pdf.text(`Battery: ${item.battery.batterySoC}%`, statusX, yOffset + 12);
          
          const networkType = getCellularNetworkType(item.cellular.cellularRat);
          pdf.text(`Signal: ${item.cellular.cellularStrength}%`, statusX, yOffset + 20);
          
          pdf.text(
            format(new Date(item.deviceLastUpdated), "MMM d, yyyy HH:mm"),
            statusX,
            yOffset + 28
          );

          yOffset += cardHeight + 10;
        } catch (error) {
          console.error("Error rendering item text:", error);
          yOffset += cardHeight + 10;
        }
      }

      pdf.save(`${deviceName || deviceId}-report.pdf`);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCSV = () => {
    if (!historyData) return;
    setIsGenerating(true);

    try {
      // Collect unique tag keys across all items, limit to 5
      const allTagKeys = [...new Set(
        filteredAndSortedHistory.flatMap(item => 
          Object.keys(item.notification?.data || {})
        )
      )].slice(0, 5);

      // Create CSV header with dynamic tag columns
      const csvHeader = [
        "Date",
        "Time",
        "Subject",
        "Reason",
        "Alert",
        "Battery",
        "Signal",
        ...allTagKeys,
      ].join(",");

      // Safe escaping function that handles null and undefined values
      const escapeCSV = (text: string | null | undefined) => {
        if (text === null || text === undefined) return '""';
        return `"${String(text).replace(/"/g, '""')}"`;
      };

      // Create CSV rows with dynamic tag values
      const csvRows = filteredAndSortedHistory.map(item => {
        const date = new Date(item.deviceLastUpdated);
        const dateFormatted = format(date, "yyyy-MM-dd");
        const timeFormatted = format(date, "HH:mm:ss");
        
        return [
          dateFormatted,
          timeFormatted,
          escapeCSV(item.notification?.subject),
          escapeCSV(item.notification?.reason),
          item.notification?.result === "true" ? "Yes" : "No",
          `${item.battery?.batterySoC || 0}%`,
          `${item.cellular?.cellularStrength || 0}%`,
          ...allTagKeys.map(key => escapeCSV(String(item.notification?.data?.[key] ?? ""))),
        ].join(",");
      });

      // Combine header and rows
      const csvContent = [csvHeader, ...csvRows].join("\n");
      
      // Create a blob with the CSV data
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      
      // Create a link to download the CSV
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${deviceName || deviceId}-data.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        description: "CSV file generated successfully",
      });
    } catch (error) {
      console.error("Error generating CSV:", error);
      toast({
        description: "Error generating CSV file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (!historyData) {
    return <div>Loading history...</div>;
  }

  // Mobile-optimized grid layout with responsive columns
  const renderMobileGrid = () => (
    <div className="grid grid-cols-2 xs:grid-cols-3 gap-2">
      {filteredAndSortedHistory.map((item, index) => (
        <div
          key={`${item.deviceLastUpdated}-${index}`}
          className={cn(
            "relative p-1 rounded-lg border transition-all",
            "hover:scale-105 hover:z-10",
            getHighlightStyle(item.notification.result, item.onDemandUserId)
          )}
          onClick={() => setSelectedImage(item)}
        >
          <div className="relative rounded-md overflow-hidden min-h-[60px]">
            <AspectRatio ratio={4/3} className="bg-muted/20">
              <img
                src={item.imageKit.thumbnailUrl || item.imageKit.url}
                alt={item.notification.subject}
                className="object-cover w-full h-full"
                loading="lazy"
              />
            </AspectRatio>
          </div>
          <div className="mt-1">
            <div className="text-xs line-clamp-1 font-medium">
              {item.notification.subject}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {format(new Date(item.deviceLastUpdated), "MM/dd HH:mm")}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Mobile-optimized list layout
  const renderMobileList = () => (
    <div className="space-y-2">
      {filteredAndSortedHistory.map((item, index) => (
        <div
          key={`${item.deviceLastUpdated}-${index}`}
          className={cn(
            "relative p-2 rounded-lg border transition-all flex gap-2",
            getHighlightStyle(item.notification.result, item.onDemandUserId)
          )}
          onClick={() => setSelectedImage(item)}
        >
          <div className="w-16 flex-shrink-0">
            <div className="relative rounded-md overflow-hidden min-h-[60px]">
              <AspectRatio ratio={4/3} className="bg-muted/20">
                <img
                  src={item.imageKit.thumbnailUrl || item.imageKit.url}
                  alt={item.notification.subject}
                  className="object-cover w-full h-full"
                  loading="lazy"
                />
              </AspectRatio>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium line-clamp-1">
              {item.notification.subject}
            </div>
            <div className="text-[10px] text-muted-foreground line-clamp-1 mt-1">
              {item.notification.reason}
            </div>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
              <div className="flex items-center gap-1">
                <Battery className="h-3 w-3" />
                {item.battery.batterySoC}%
              </div>
              <div>
                {format(new Date(item.deviceLastUpdated), "MM/dd HH:mm")}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Standard desktop grid layout
  const renderGridLayout = () => (
    <div className="grid grid-cols-5 gap-4">
      {filteredAndSortedHistory.map((item, index) => (
        <div
          key={`${item.deviceLastUpdated}-${index}`}
          className={cn(
            "relative p-3 rounded-lg border transition-all hover:shadow-lg group",
            "hover:scale-105 hover:z-10",
            item.notification.result?.toLowerCase() === "true" ? "brand-alert-tile border-red-500 bg-red-50" : 
            (item.notification.result === "false" && item.onDemandUserId && item.onDemandUserId !== "NULL") ? "brand-false-alert-tile border-blue-500 bg-blue-50" : "border-border bg-card"
          )}
        >
          <div className={cn(
            "absolute top-2 right-2 z-10 transition-opacity",
            selectedItems.has(`${item.deviceLastUpdated}`) ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}>
            <Checkbox
              checked={selectedItems.has(`${item.deviceLastUpdated}`)}
              onCheckedChange={(checked) => 
                handleCheckboxChange(`${item.deviceLastUpdated}`, checked === true)
              }
            />
          </div>

          <div 
            className="cursor-pointer"
            onClick={() => setSelectedImage(item)}
          >
            <div className="relative aspect-video rounded-md overflow-hidden">
              <img
                src={item.imageKit.thumbnailUrl || item.imageKit.url}
                alt={item.notification.subject}
                className="object-cover w-full h-full"
              />
            </div>
          </div>

          <div className="mt-2 space-y-1">
            <h3 className="text-sm font-medium line-clamp-1">
              {item.notification.subject}
            </h3>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {item.notification.reason}
            </p>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
              <div className="flex items-center gap-1">
                <Battery className="h-3 w-3" />
                {item.battery.batterySoC}%
              </div>
              <div className="flex items-center gap-1">
                <Signal className="h-3 w-3" />
                {item.cellular.cellularStrength}%
                <span className="text-[10px] font-medium">
                  {getCellularNetworkType(item.cellular.cellularRat)}
                </span>
              </div>
            </div>

            <div className="text-[10px] text-muted-foreground mt-1">
              {format(new Date(item.deviceLastUpdated), "MMM d, yyyy HH:mm")}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Standard desktop list layout
  const renderListLayout = () => (
    <div className="space-y-4">
      {filteredAndSortedHistory.map((item, index) => (
        <div
          key={`${item.deviceLastUpdated}-${index}`}
          className={cn(
            "relative p-4 rounded-lg border transition-all hover:shadow-lg group",
            "hover:scale-[1.01] hover:z-10 flex gap-4",
                    getHighlightStyle(item.notification.result, item.onDemandUserId)
          )}
        >
          <div className={cn(
            "absolute top-4 right-4 z-10 transition-opacity",
            selectedItems.has(`${item.deviceLastUpdated}`) ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}>
            <Checkbox
              checked={selectedItems.has(`${item.deviceLastUpdated}`)}
              onCheckedChange={(checked) => 
                handleCheckboxChange(`${item.deviceLastUpdated}`, checked === true)
              }
            />
          </div>

          <div 
            className="cursor-pointer w-48 flex-shrink-0"
            onClick={() => setSelectedImage(item)}
          >
            <div className="relative aspect-video rounded-md overflow-hidden">
              <img
                src={item.imageKit.thumbnailUrl || item.imageKit.url}
                alt={item.notification.subject}
                className="object-cover w-full h-full"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-base font-medium">
                  {item.notification.subject}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {item.notification.reason}
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                {format(new Date(item.deviceLastUpdated), "MMM d, yyyy HH:mm")}
              </div>
            </div>
            
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Battery className="h-4 w-4" />
                {item.battery.batterySoC}%
              </div>
              <div className="flex items-center gap-1">
                <Signal className="h-4 w-4" />
                {item.cellular.cellularStrength}%
                <span className="text-xs font-medium">
                  {getCellularNetworkType(item.cellular.cellularRat)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Standard desktop timeline layout
  const renderTimelineLayout = () => (
    <div className="relative w-full">
      <div className="max-w-full overflow-x-auto">
        <div className="flex gap-4 min-h-[12rem] py-4 items-end" style={{ minWidth: 'max-content' }}>
          {filteredAndSortedHistory.map((item, index) => {
            const isAlert = item.notification.result?.toLowerCase() === "true";
            const isOnDemand = item.notification.result === "false" && item.onDemandUserId && item.onDemandUserId !== "NULL";
            return (
              <div
                key={`${item.deviceLastUpdated}-${index}`}
                className={cn(
                  "relative transition-all group",
                  isAlert ? "w-40 sm:w-64" : "w-36 sm:w-48", // Smaller on mobile
                )}
              >
                <div
                  className={cn(
                    "p-3 rounded-lg border transition-all hover:shadow-lg mb-8",
                    "hover:scale-105 hover:z-10",
                    getHighlightStyle(item.notification.result, item.onDemandUserId)
                  )}
                >
                  <div className={cn(
                    "absolute top-2 right-2 z-10 transition-opacity",
                    selectedItems.has(`${item.deviceLastUpdated}`) ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                  )}>
                    <Checkbox
                      checked={selectedItems.has(`${item.deviceLastUpdated}`)}
                      onCheckedChange={(checked) => 
                        handleCheckboxChange(`${item.deviceLastUpdated}`, checked === true)
                      }
                    />
                  </div>

                  <div 
                    className="cursor-pointer"
                    onClick={() => setSelectedImage(item)}
                  >
                    <div className="relative aspect-video rounded-md overflow-hidden">
                      <img
                        src={item.imageKit.thumbnailUrl || item.imageKit.url}
                        alt={item.notification.subject}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  </div>

                  <div className="mt-2 space-y-1">
                    <h3 className="text-sm font-medium line-clamp-1">
                      {item.notification.subject}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {item.notification.reason}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                      <div className="flex items-center gap-1">
                        <Battery className="h-3 w-3" />
                        {item.battery.batterySoC}%
                      </div>
                      <div className="flex items-center gap-1">
                        <Signal className="h-3 w-3" />
                        {item.cellular.cellularStrength}%
                        <span className="text-[10px] font-medium">
                          ({getCellularNetworkType(item.cellular.cellularRat)})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="h-4 border-l border-border mx-auto"></div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {format(new Date(item.deviceLastUpdated), "MMM d, HH:mm")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Conditional content based on mobile view
  if (isMobileView) {
    return (
      <div className="w-full space-y-4 animate-fade-in">
        {layoutType === "grid" && renderMobileGrid()}
        {layoutType === "list" && renderMobileList()}

        {/* Hide the alerts filter checkbox for mobile view - it's already in DevicePastImages */}
        <div className="hidden flex items-center justify-end space-x-2 mt-2">
          <Checkbox 
            id="mobileAlertsFilter" 
            checked={showOnlyAlerts}
            onCheckedChange={(checked) => setShowOnlyAlerts(checked as boolean)}
          />
          <label
            htmlFor="mobileAlertsFilter"
            className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show alerts only
          </label>
        </div>

        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-md w-[95vw] max-h-[90vh] p-4 flex flex-col" aria-describedby="image-dialog-description">
            <DialogHeader>
              <DialogTitle className="text-sm">Device Image</DialogTitle>
              <DialogDescription id="image-dialog-description" className="text-xs">
                {selectedImage?.notification.subject}
              </DialogDescription>
            </DialogHeader>
            
            {selectedImage && (
              <div className="flex flex-col gap-2 overflow-hidden">
                <div className="relative rounded-lg overflow-hidden">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white h-8 w-8 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviousImage();
                    }}
                    disabled={filteredAndSortedHistory.indexOf(selectedImage) === 0}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>

                  <div className="flex items-center justify-center">
                    <img
                      src={selectedImage.imageKit.url}
                      alt="Full size"
                      className="object-contain max-h-[55vh] w-auto"
                    />
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white h-8 w-8 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    disabled={filteredAndSortedHistory.indexOf(selectedImage) === filteredAndSortedHistory.length - 1}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </div>
                
                <div className="text-xs text-muted-foreground">
                  {selectedImage.notification.reason}
                </div>
                
                {(() => {
                  const tagEntries = normalizeTagData(selectedImage.notification.data);
                  if (tagEntries.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {tagEntries.slice(0, 5).map(([key, value]) => (
                        <Badge key={key} variant="secondary" className="text-xs">
                          {key}: {String(value)}
                        </Badge>
                      ))}
                    </div>
                  );
                })()}
                
                <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t mt-auto">
                  <div className="flex items-center gap-2">
                    <Battery className="h-3 w-3" />
                    {selectedImage.battery.batterySoC}%
                    <Signal className="h-3 w-3" />
                    {selectedImage.cellular.cellularStrength}%
                  </div>
                  <div>
                    {format(new Date(selectedImage.deviceLastUpdated), "MMM d, HH:mm")}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <DownloadImageButton deviceId={deviceId} item={selectedImage} compact />
                  {(isSuperuser || isAdmin) && (
                    <ReprocessImageDialog deviceId={deviceId} item={selectedImage} compact />
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Desktop layout with all features
  return (
    <div className="w-full space-y-8 animate-fade-in">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="border rounded-md p-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLayoutType("grid")}
              className={cn(
                "gap-2",
                layoutType === "grid" 
                  ? "text-black border-[#DFEF97]" 
                  : ""
              )}
              style={layoutType === "grid" ? { backgroundColor: '#DFEF97' } : {}}
            >
              <LayoutGrid className="h-4 w-4" />
              Grid
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLayoutType("list")}
              className={cn(
                "gap-2",
                layoutType === "list" 
                  ? "text-black border-[#DFEF97]" 
                  : ""
              )}
              style={layoutType === "list" ? { backgroundColor: '#DFEF97' } : {}}
            >
              <List className="h-4 w-4" />
              List
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLayoutType("timeline")}
              className={cn(
                "gap-2",
                layoutType === "timeline" 
                  ? "text-black border-[#DFEF97]" 
                  : ""
              )}
              style={layoutType === "timeline" ? { backgroundColor: '#DFEF97' } : {}}
            >
              <History className="h-4 w-4" />
              Timeline
            </Button>
          </div>

          <div className="border rounded-lg p-4 bg-muted/20">
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2 w-full">
                <Button onClick={generatePDF} variant="outline" className="gap-2 w-full" disabled={isGenerating}>
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4" />
                  )}
                  {isGenerating ? "Generating..." : selectedItems.size > 0 ? `PDF Selected (${selectedItems.size})` : "Download PDF"}
                </Button>
                <Button onClick={generateCSV} variant="outline" className="gap-2 w-full" disabled={isGenerating}>
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  {isGenerating ? "Generating..." : "Download CSV"}
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="highlightAlertsPdf"
                  checked={highlightAlerts}
                  onCheckedChange={(checked) => setHighlightAlerts(checked === true)}
                />
                <label
                  htmlFor="highlightAlertsPdf"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Highlight alerts in PDF
                </label>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleSelectAll} 
                  variant="outline" 
                  className="gap-2"
                  disabled={filteredAndSortedHistory.length === 0}
                >
                  <Checkbox 
                    checked={
                      filteredAndSortedHistory.length > 0 &&
                      filteredAndSortedHistory.every(item => 
                        selectedItems.has(`${item.deviceLastUpdated}`)
                      )
                    }
                  />
                  Select All
                </Button>
                <Button 
                  onClick={downloadSelectedImages} 
                  variant="outline" 
                  className="gap-2" 
                  disabled={isDownloading || selectedItems.size === 0}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {isDownloading ? "Downloading..." : `Download Selected (${selectedItems.size})`}
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="gap-2" 
                      disabled={isGeneratingTimelapse || selectedItems.size === 0}
                      onClick={(e) => {
                        // Stop event propagation to prevent parent click events
                        e.stopPropagation();
                      }}
                    >
                      {isGeneratingTimelapse ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Video className="h-4 w-4" />
                      )}
                      {isGeneratingTimelapse ? "Generating..." : "Create Timelapse"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-56 pointer-events-auto" 
                    style={{ zIndex: 100 }}
                    onClick={(e) => {
                      // Stop event propagation to prevent parent click events
                      e.stopPropagation();
                    }}
                  >
                    <div className="space-y-4">
                      <div className="font-medium">Timelapse Quality</div>
                      <div className="space-y-2">
                        <Select 
                          value={timelapseQuality} 
                          onValueChange={(value) => setTimelapseQuality(value as 'low' | 'medium' | 'high' | 'ultra')}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select quality" />
                          </SelectTrigger>
                          <SelectContent className="pointer-events-auto">
                            <SelectItem value="low">Low (Smaller file)</SelectItem>
                            <SelectItem value="medium">Medium (Default)</SelectItem>
                            <SelectItem value="high">High (Better quality)</SelectItem>
                            <SelectItem value="ultra">Ultra (Original size)</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button 
                          onClick={(e) => {
                            e.stopPropagation();
                            generateTimelapseVideo();
                          }}
                          className="w-full text-black"
                          style={{ backgroundColor: '#DFEF97' }}
                          disabled={isGeneratingTimelapse}
                        >
                          {isGeneratingTimelapse ? "Generating..." : "Generate"}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Popover open={startDatePopoverOpen} onOpenChange={setStartDatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[120px] justify-start text-left font-normal",
                  !pendingStartDate && "text-muted-foreground"
                )}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {pendingStartDate ? format(pendingStartDate, "dd/MM/yy") : <span>Start date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <CalendarComponent
                mode="single"
                selected={pendingStartDate}
                onSelect={handleStartDateChange}
                disabled={(date) =>
                  !minDate ||
                  !maxDate ||
                  date < minDate ||
                  date > maxDate
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Popover open={endDatePopoverOpen} onOpenChange={setEndDatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[120px] justify-start text-left font-normal",
                  !pendingEndDate && "text-muted-foreground"
                )}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {pendingEndDate ? format(pendingEndDate, "dd/MM/yy") : <span>End date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <CalendarComponent
                mode="single"
                selected={pendingEndDate}
                onSelect={handleEndDateChange}
                disabled={(date) =>
                  !minDate ||
                  !maxDate ||
                  date < minDate ||
                  date > maxDate ||
                  (pendingStartDate && date < pendingStartDate)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {(pendingStartDate || pendingEndDate) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearDateRange}
              className="h-9 w-9"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear date range</span>
            </Button>
          )}
          <Select 
            value={itemLimit} 
            onValueChange={setItemLimit}
            disabled={!!(startDate && endDate)}
          >
            <SelectTrigger className={cn(
              "w-[120px]",
              (startDate && endDate) && "opacity-50 cursor-not-allowed"
            )}>
              <SelectValue placeholder="Show items" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 items</SelectItem>
              <SelectItem value="25">25 items</SelectItem>
              <SelectItem value="50">50 items</SelectItem>
              <SelectItem value="100">100 items</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="desktopAlertsFilter" 
              checked={showOnlyAlerts}
              onCheckedChange={(checked) => setShowOnlyAlerts(checked as boolean)}
            />
            <label
              htmlFor="desktopAlertsFilter"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Alerts
            </label>
          </div>
        </div>
      </div>

      {layoutType === "grid" && renderGridLayout()}
      {layoutType === "list" && renderListLayout()}
      {layoutType === "timeline" && renderTimelineLayout()}

      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent 
          className="w-[95vw] max-w-4xl max-h-[90vh] p-6 flex flex-col gap-4 overflow-hidden" 
          aria-describedby="image-dialog-description"
        >
          <DialogHeader>
            <DialogTitle>Device Image Gallery</DialogTitle>
            <DialogDescription id="image-dialog-description">
              Use arrow buttons or keyboard arrows to navigate through images.
            </DialogDescription>
          </DialogHeader>
          
          {selectedImage && (
            <div className="flex flex-col gap-4 h-full overflow-hidden">
              <div className="relative rounded-lg overflow-hidden flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white z-10"
                  onClick={handlePreviousImage}
                  disabled={filteredAndSortedHistory.indexOf(selectedImage) === 0}
                >
                  <ChevronLeft className="h-8 w-8" />
                  <span className="sr-only">Previous image</span>
                </Button>

                <Zoom>
                  <img
                    src={selectedImage.imageKit.url}
                    alt="Full size"
                    className="object-contain max-h-[60vh] max-w-full cursor-zoom-in"
                  />
                </Zoom>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white z-10"
                  onClick={handleNextImage}
                  disabled={filteredAndSortedHistory.indexOf(selectedImage) === filteredAndSortedHistory.length - 1}
                >
                  <ChevronRight className="h-8 w-8" />
                  <span className="sr-only">Next image</span>
                </Button>
              </div>
              
              <div className="space-y-4 p-4 bg-muted/20 rounded-lg mt-auto">
                <h3 className="text-xl font-semibold">{selectedImage.notification.subject}</h3>
                <p className="text-sm text-muted-foreground">{selectedImage.notification.reason}</p>
                {(() => {
                  const tagEntries = normalizeTagData(selectedImage.notification.data);
                  if (tagEntries.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-2">
                      {tagEntries.slice(0, 5).map(([key, value]) => (
                        <Badge key={key} variant="secondary" className="text-xs">
                          {key}: {String(value)}
                        </Badge>
                      ))}
                    </div>
                  );
                })()}
                <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Battery className="h-4 w-4" />
                    {selectedImage.battery.batterySoC}%
                    <Signal className="h-4 w-4 ml-2" />
                    {selectedImage.cellular.cellularStrength}%
                    <span className="text-xs">
                      (Image {filteredAndSortedHistory.findIndex(item => 
                        item.deviceLastUpdated === selectedImage.deviceLastUpdated
                      ) + 1} of {filteredAndSortedHistory.length})
                    </span>
                  </div>
                  <div>
                    {format(new Date(selectedImage.deviceLastUpdated), "PPP p")}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <DownloadImageButton deviceId={deviceId} item={selectedImage} />
                  {(isSuperuser || isAdmin) && (
                    <ReprocessImageDialog deviceId={deviceId} item={selectedImage} />
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GridLayout;
